import React, { useMemo, useEffect } from 'react';
import { RiskAlertsWidget } from './RiskAlertsWidget';
import { useQuery } from '@apollo/client';
import { useAtomValue } from 'jotai';
import {
  latestSnapshotIdAtom,
  triggerHardRefetchAtom,
  selectedSectorFilterAtom,
  portfolioNameAtom,
} from '~/atoms/dashboardAtom';
import type { RecommendationReport } from '../RecommendationReportWidget/pdfGenerator';
import {
  GET_AI_ANALYSIS,
  GET_AI_ANALYSIS_ALL_SECTORS,
} from '~/graphql/queries/portfolio';
import { GET_INSTRUMENTS_BY_TICKERS } from '~/graphql/queries/instruments';

interface DataPoint {
  value: string;
  metric: string;
  concern: string;
  threshold: string;
}

interface RiskAlert {
  level: 'high' | 'medium' | 'low';
  title: string;
  message: string;
  urgency: 'immediate' | 'monitor';
  confidence: number;
  data_points?: DataPoint[];
  affected_positions?: string[];
  extended_reasoning?: string;
  recommended_action?: string;
}

interface AIAnalysisData {
  risk_alerts: RiskAlert[];
  recommendation_report?: string | null;
}

interface AIAnalysisResponse {
  EQPortfoliosAIAnalysis: {
    analysis_data: AIAnalysisData;
    createdAt: string;
  }[];
}

export interface RiskAlertsWidgetWrapperProps {
  widgetId: string;
  handleRemoveWidget?: (widgetId: string) => void;
  shortDescription?: string;
}

export const RiskAlertsWidgetWrapper: React.FC<
  RiskAlertsWidgetWrapperProps
> = ({ widgetId, handleRemoveWidget, shortDescription }) => {
  const latestSnapshotId = useAtomValue(latestSnapshotIdAtom);
  const triggerHardRefetch = useAtomValue(triggerHardRefetchAtom);
  const selectedSectorFilter = useAtomValue(selectedSectorFilterAtom);
  const portfolioName = useAtomValue(portfolioNameAtom);
  const snapshotId = latestSnapshotId;

  // Choose query based on whether sectors are selected
  const query =
    selectedSectorFilter.length > 0
      ? GET_AI_ANALYSIS
      : GET_AI_ANALYSIS_ALL_SECTORS;
  const variables =
    selectedSectorFilter.length > 0
      ? { snapshotId, sectors: selectedSectorFilter }
      : { snapshotId };

  const { data, loading, error, refetch } = useQuery<AIAnalysisResponse>(
    query,
    {
      variables,
      notifyOnNetworkStatusChange: true,
      skip: !snapshotId,
    },
  );

  // Automatically refetch when snapshotId changes
  useEffect(() => {
    if (snapshotId) {
      refetch();
    }
  }, [snapshotId, refetch]);

  // Force hard refetch when triggered by other widgets
  useEffect(() => {
    if (triggerHardRefetch > 0) {
      refetch();
    }
  }, [triggerHardRefetch, refetch]);

  const riskAlerts = useMemo(() => {
    if (
      loading ||
      !data?.EQPortfoliosAIAnalysis?.[0]?.analysis_data?.risk_alerts
    ) {
      return [];
    }

    return data.EQPortfoliosAIAnalysis[0].analysis_data.risk_alerts.map(
      (alert) => ({
        title: alert.title,
        percentage: `${alert.confidence}%`,
        description: alert.message,
        level: alert.level,
        data_points: alert.data_points,
        affected_positions: alert.affected_positions,
        extended_reasoning: alert.extended_reasoning,
        recommended_action: alert.recommended_action,
      }),
    );
  }, [data, loading]);

  // Collect all unique tickers from affected_positions across all alerts
  const allTickers = useMemo(() => {
    const tickers = new Set<string>();
    riskAlerts.forEach((alert) => {
      if (alert.affected_positions) {
        alert.affected_positions.forEach((ticker) => {
          if (ticker && ticker.trim() !== '') {
            tickers.add(ticker.trim());
          }
        });
      }
    });
    return Array.from(tickers);
  }, [riskAlerts]);

  // Fetch instrument data for tickers
  const shouldSkipInstrumentQuery =
    allTickers.length === 0 ||
    !allTickers.some((ticker) => ticker && ticker.trim() !== '');

  const { data: instrumentsData } = useQuery(GET_INSTRUMENTS_BY_TICKERS, {
    variables: { tickers: allTickers },
    skip: shouldSkipInstrumentQuery,
    notifyOnNetworkStatusChange: true,
  });

  // Create a map of ticker -> company name
  // Handles various ticker formats by normalizing and mapping multiple variations
  const tickerToCompanyName = useMemo(() => {
    const map = new Map<string, string>();
    if (instrumentsData?.EQInstrument) {
      instrumentsData.EQInstrument.forEach(
        (instrument: { full_ticker: string; ticker: string; name: string }) => {
          if (!instrument.name) return;

          // Map full_ticker to company name (exact match)
          if (instrument.full_ticker) {
            const normalizedFull = instrument.full_ticker.toUpperCase().trim();
            map.set(normalizedFull, instrument.name);

            // Also map base ticker (before colon, dot, or space)
            const baseFromFull =
              normalizedFull.split(':')[0] ||
              normalizedFull.split('.')[0] ||
              normalizedFull.split(' ')[0];
            if (baseFromFull && baseFromFull !== normalizedFull) {
              map.set(baseFromFull, instrument.name);
            }

            // Also map alphanumeric-only version (remove all non-alphanumeric)
            const alphanumeric = normalizedFull.replace(/[^A-Z0-9]/g, '');
            if (alphanumeric && alphanumeric !== normalizedFull) {
              map.set(alphanumeric, instrument.name);
            }
          }

          // Map ticker to company name (if different from full_ticker)
          if (instrument.ticker) {
            const normalizedTicker = instrument.ticker.toUpperCase().trim();
            if (!map.has(normalizedTicker)) {
              map.set(normalizedTicker, instrument.name);
            }
          }
        },
      );
    }
    return map;
  }, [instrumentsData]);

  const recommendationReport = useMemo<RecommendationReport | null>(() => {
    if (!data?.EQPortfoliosAIAnalysis?.[0]) return null;
    const entry = data.EQPortfoliosAIAnalysis[0];
    const reportContent = entry.analysis_data.recommendation_report?.trim();
    if (!reportContent) return null;

    return {
      id: `risk-alerts-report-${entry.createdAt || 'latest'}`,
      title: 'AI Portfolio Analysis Report',
      content: reportContent,
      key_findings: [],
      recommendations: [],
      generated_at: entry.createdAt || new Date().toISOString(),
      analyst_notes: '',
      risk_alerts: entry.analysis_data.risk_alerts,
    };
  }, [data]);

  // Format and validate timestamp
  const formattedTimestamp = useMemo(() => {
    const timestamp = data?.EQPortfoliosAIAnalysis?.[0]?.createdAt;

    if (!timestamp) return 'N/A';

    try {
      // This matches ISO 8601 format with timezone indicators
      const hasTimezoneInfo = /[+-]\d{2}:\d{2}$|Z$/i.test(timestamp);

      // If no timezone info, assume it's UTC and add Z
      const utcTimestamp = hasTimezoneInfo ? timestamp : `${timestamp}Z`;

      const date = new Date(utcTimestamp);

      // Check if it's a valid date
      if (isNaN(date.getTime())) {
        return 'Invalid Date';
      }

      // Format according to user's local timezone and time format preference
      // Use the browser's default time format (respects user's system settings)
      const formatted = date.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        // hour12: undefined - let the browser decide based on user's locale/system settings
      });

      return formatted;
    } catch (error) {
      return 'Invalid Date';
    }
  }, [data]);

  return (
    <RiskAlertsWidget
      riskAlerts={riskAlerts}
      isLoading={loading}
      error={error || null}
      refetch={refetch}
      handleRemoveWidget={handleRemoveWidget}
      widgetId={widgetId}
      title={shortDescription}
      timestamp={formattedTimestamp}
      snapshotId={snapshotId}
      report={recommendationReport}
      portfolioName={portfolioName}
      tickerToCompanyName={tickerToCompanyName}
    />
  );
};
